// ═══════════════════════════════════════════════════════════════════════════
// AI-callable safe-contour guard — BASE-PRIMITIVE + BASE-MODIFY (CC-2c sub-arm 4)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors the block-domain `assertAiCallableAction` (aiCallableGuard.ts) and the
// draw-modify `assertAiCallableDrawAction` (drawModifyGuard.ts): maps every
// base-primitive / base-modify AI action to the user-facing registry command it
// targets, then reuses the CC-2a generic `assertAiCallableCommand` to fail
// CLOSED when that target is not `aiCallable: true` (or is absent from the
// registry).
//
// The guard is ACTION-level, not literal-level: the registry descriptor the
// action targets is checked (e.g. CREATE_LINE → "LINE", MOVE → "MOVE"), never
// the internal EditorCommand literal the translator emits
// (`geometry/create-line`, `geometry/move-entities`). This is the same rule the
// block / draw-modify guards document — the AI-callable surface is the command
// the AI is asking for.
//
// POINT / ELLIPSE are deliberately ABSENT from the action union (they are
// `aiCallable: false` in the registry — drawing.ts:119 / 494 — and not in
// ТЗ §10.1). They never reach this guard; if a future action targeted them the
// guard would still fail closed because the target descriptor is aiCallable:false.

import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
  type CommandRegistryId,
} from "@dxf-ai-editor/command-registry";

import { assertAiCallableCommand } from "./aiCallableGuard";
import type { BasePrimDomainAiCommand } from "./basePrimSchemas";

/**
 * Maps every base-primitive / base-modify AI action to the registry command it
 * targets. The guard resolves this id and checks its `aiCallable` flag, so an
 * action whose target is ever flipped to `aiCallable: false` fails closed
 * automatically.
 *
 * `Record<BasePrimDomainAiCommand["action"], …>` forces exhaustiveness: adding
 * a new action without declaring its target command is a compile error. Each
 * target is `aiCallable: true` in the registry today and listed AI-callable in
 * ТЗ §10.1.
 */
export const BASE_PRIM_AI_ACTION_TARGET_COMMAND: Record<
  BasePrimDomainAiCommand["action"],
  CommandRegistryId
> = {
  CREATE_LINE: "LINE",
  CREATE_CIRCLE: "CIRCLE",
  CREATE_ARC: "ARC",
  CREATE_PLINE: "PLINE",
  MOVE: "MOVE",
  COPY: "COPY",
  ROTATE: "ROTATE",
  DELETE_ENTITIES: "ERASE",
};

/**
 * Guard a single base-primitive / base-modify AI action: resolves its target
 * registry command and asserts AI-callability. Throws `NonAiCallableCommandError`
 * when the target is not AI-callable (fail-closed). Called by
 * `translateBasePrimCommand` before any EditorCommand is produced.
 */
export function assertAiCallableBasePrimAction(
  action: BasePrimDomainAiCommand["action"],
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  assertAiCallableCommand(
    BASE_PRIM_AI_ACTION_TARGET_COMMAND[action],
    descriptors,
  );
}
